import React, { useEffect, useState, useRef } from "react";
import {
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	IconButton,
	Box,
} from "@mui/material";
import Typography from '../../typography/typography';
import './dynamic-report.scss'
import { DynamicRow } from "./dynamic-rows";
import { useTranslation } from "react-i18next";
import { formateValueByType, renderEmptyRowsFallback } from "../../../utils/common-utility";
import SearchBar from "../../search-bar/search-bar";
import dayjs from "dayjs";
import { Checkbox } from '@mui/material';
import { ArrowDown } from "../../icons";
import { ArrowUp } from "../../icons";
import {
	calculateColumnWidths,
	sampleRowContent,
	DEFAULT_MEASUREMENT_CONFIG,
	getCheckboxColumnWidth
} from "../../../utils/text-measurement";

interface IReportTableProps {
	data: any[];
	headers: any[];
	tableHeaders: any[];
	totalHeaders?: any[];
	handleSort?: (data: string, direction: string) => void;
	collapsLevel?: number,
	enableSearch?: boolean,
	headerHeight?: string,
	isIncrese?: boolean,
	onSelectionChange?: (selectedData: any[]) => void;
	enableCheckbox?: boolean;
	afterHeader?: React.ReactNode | (() => React.ReactNode);
	totalKey?: string
	rowHeight?: string
}

// Updated DynamicRow interface and component in separate file
export interface IDynamicRowProps {
	rowData: any;
	level: number;
	tableHeaders: any[];
	totalHeaders: any[];
	headers: any[];
	collapsAll: boolean;
	collapsLevel: number;
	isIncrese: boolean,
	data: any,
	rowPath: number[],
	selectedRowPaths: number[][],
	onCheckboxChange: (rowPath: number[], checked: boolean) => void;
	dataSlice: any[];
	dataIndex: number;
	enableCheckBox: boolean
	rowHeight?: string
	columnWidths?: { [key: string]: number }
}

export function ReportTable({
	headers,
	data,
	tableHeaders,
	totalHeaders = [],
	handleSort,
	collapsLevel = 0,
	isIncrese,
	enableSearch = true,
	headerHeight = '16px',
	onSelectionChange,
	enableCheckbox = false,
	afterHeader,
	totalKey = 'total',
	rowHeight = 'auto'
}: IReportTableProps) {

	type HeaderType = {
		type?: string;
		[key: string]: any;
	};
	const [filter, setFilter] = useState(null)
	const [baseData, setBaseData] = useState<any>(null);
	const [formatedData, setFormatedData] = useState<any>(null);
	const [searchQueries, setSearchQueries] = useState<any>({});
	const { t } = useTranslation()
	const [selectedRowPaths, setSelectedRowPaths] = useState<number[][]>([]);
	const [columnWidths, setColumnWidths] = useState<{ [key: string]: number }>({});
	const [resizingColumn, setResizingColumn] = useState<string | null>(null);
	const resizeStartX = useRef<number>(0);
	const resizeStartWidth = useRef<number>(0);
	const headerRefs = useRef<{ [key: string]: HTMLElement | null }>({});
	const [isInitialized, setIsInitialized] = useState(false);
	const [isSorting, setIsSorting] = useState(false);

	function getNestedValue(obj: any, path: any) {
		if (path?.name == '') {
			return ''
		}

		const formattedValue = path?.name?.split('.').reduce((acc: any, key: any) => (acc && acc[key] !== undefined ? acc[key] : undefined), obj) || (path?.name.split('.').reduce((acc: any, key: any) => (acc && acc[key] !== undefined ? acc[key] : undefined), obj) == '0' ? '0' : '-');
		return formateValueByType({
			value: formattedValue,
			...path
		})
	}
	// Initialize column widths based on content
	useEffect(() => {
		if (!isInitialized && data && headers.length > 0 && !isSorting) {
			// Sample row content for width calculation
			const rowContentSamples = sampleRowContent({
				maxRows: 9999,
				headers,
				data: Array.isArray(data) ? data : data?.[tableHeaders[0]?.rowKey] || [],
				tableHeaders,
				t,
			});

			// Calculate optimal widths based on header and content
			const calculatedWidths = calculateColumnWidths(
				headers,
				rowContentSamples,
				DEFAULT_MEASUREMENT_CONFIG,
				{ min: 100, padding: 32, max: 400 },
				t
			);
			setColumnWidths(calculatedWidths);
			setIsInitialized(true);
		}
		//eslint-disable-next-line
	}, [data, headers, isInitialized, isSorting, tableHeaders]);
	//  useEffect(() => {
	//     if (!isInitialized && data && headers.length > 0) {
	//         const initialWidths: { [key: string]: number } = {};

	//         headers.forEach((header) => {
	//             // Check if width is already specified in header config
	//             if (header.width) {
	//                 const numericWidth = typeof header.width === 'string'
	//                     ? parseInt(header.width.replace(/[^\d]/g, ''))
	//                     : header.width;
	//                 initialWidths[header.name] = numericWidth;
	//             } else {
	//                 // Calculate width based on header ref if available
	//                 const headerElement = headerRefs.current[header.name];
	//                 if (headerElement) {
	//                     const contentWidth = headerElement.scrollWidth;
	//                     // Add some padding (e.g., 48px for icons and padding)
	//                     initialWidths[header.name] = Math.max(100, contentWidth + 48);
	//                 } else {
	//                     // Default width
	//                     initialWidths[header.name] = 100;
	//                 }
	//             }
	//         });
	//         setColumnWidths(initialWidths);
	//         setIsInitialized(true);
	//     }
	// }, [data, headers, isInitialized]);

	function getAllRowPaths(data: any, tableHeaders: any[], rowKey: string, currentPath: number[] = [], level: number = 0): number[][] {
		if (!Array.isArray(data)) return [];
		let paths: number[][] = [];
		data.forEach((row: any, idx: number) => {
			const path = [...currentPath, idx];
			if (tableHeaders[level]?.enableCheckbox) {
				paths.push(path);
			}
			const nextLevel = level + 1;
			const nextRowKey = tableHeaders[nextLevel]?.rowKey;
			if (nextRowKey && Array.isArray(row[nextRowKey])) {
				paths = paths.concat(getAllRowPaths(row[nextRowKey], tableHeaders, nextRowKey, path, nextLevel));
			}
		});
		return paths;
	}

	const handleCheckboxChange = (rowPath: number[], checked: boolean) => {
		setSelectedRowPaths(prev => {
			const next = checked
				? [...prev, rowPath]
				: prev.filter(path => !(path.length === rowPath.length && path.every((v, i) => v === rowPath[i])));
			return next;
		});
	};

	const handleHeaderCheckboxChange = (checked: boolean) => {
		const allPaths = getAllRowPaths(
			Array.isArray(formatedData) ? formatedData : formatedData?.[tableHeaders[0]?.rowKey],
			tableHeaders,
			tableHeaders[0]?.rowKey
		);
		if (checked) {
			setSelectedRowPaths(allPaths);
		} else {
			setSelectedRowPaths([]);
		}
	};

	const allRowPaths = getAllRowPaths(
		Array.isArray(formatedData) ? formatedData : formatedData?.[tableHeaders[0]?.rowKey],
		tableHeaders,
		tableHeaders[0]?.rowKey
	);
	const allSelected = allRowPaths.length > 0 && allRowPaths.every(path =>
		selectedRowPaths.some(sel => sel.length === path.length && sel.every((v, i) => v === path[i]))
	);
	const someSelected = allRowPaths.some(path =>
		selectedRowPaths.some(sel => sel.length === path.length && sel.every((v, i) => v === path[i]))
	);

	function searchReportNestedByKey(
		obj: any,
		query: string,
		keysToSearch: string | string[] | null,
		header: HeaderType
	): boolean {
		if (obj == null) return false;
		const queryLower = query.toLowerCase();
		if (typeof keysToSearch === 'string') {
			keysToSearch = [keysToSearch];
		}
		if (header?.type == 'date') {
			return dayjs(obj).format('DD-MM-YYYY').includes(queryLower);
		}

		if (typeof obj === 'string') {
			return obj.toLowerCase().includes(queryLower);
		}

		if (typeof obj === 'object') {
			if (Array.isArray(obj)) {
				return obj.some(item => searchReportNestedByKey(item, queryLower, keysToSearch, header));
			}
			if (keysToSearch) {
				return keysToSearch.some(key => {
					const val = obj[key];
					if (val != null) {
						return typeof val === 'string' ? val.toLowerCase().includes(queryLower) : searchReportNestedByKey(val, queryLower, null, header);
					}
					const nestedObj = Object.keys(obj).filter(k => typeof obj[k] === 'object' && obj[k] !== null);
					if (nestedObj.length > 0) {
						return nestedObj.some(n => searchReportNestedByKey(obj[n], queryLower, null, header));
					}
					return false;
				});
			} else {
				return Object.values(obj).some(val => searchReportNestedByKey(val, queryLower, null, header));
			}
		}

		return String(obj).toLowerCase().includes(queryLower);
	}

	useEffect(() => {
		let filteredData = Array.isArray(baseData) ? baseData : baseData?.[tableHeaders[0]?.rowKey];
		Object.keys(searchQueries)?.forEach(key => {
			const query = searchQueries?.[key]?.query?.toLowerCase();
			if (query) {
				filteredData = filteredData?.filter((item: any) => searchReportNestedByKey(item, query, key, searchQueries?.[key]?.header));
			}
		});
		setFormatedData(Array.isArray(baseData) ? filteredData : { ...baseData, [tableHeaders[0]?.rowKey]: filteredData });
		//eslint-disable-next-line
	}, [searchQueries, baseData]);

	const handleSearch = (searchQuery: string, key: string, header: any) => {
		setSearchQueries((prev: any) => ({
			...prev,
			[key]: {
				query: searchQuery,
				header
			},
		}));
	};

	// Column resize handlers
	const handleMouseDown = (e: React.MouseEvent, columnName: string) => {
		e.preventDefault();
		e.stopPropagation();
		setResizingColumn(columnName);
		resizeStartX.current = e.clientX;
		resizeStartWidth.current = columnWidths[columnName] || 100;
	};

	const handleMouseMove = (e: MouseEvent) => {
		if (!resizingColumn) return;
		const diff = e.clientX - resizeStartX.current;
		const newWidth = Math.max(100, resizeStartWidth.current + diff);
		setColumnWidths(prev => ({
			...prev,
			[resizingColumn]: newWidth
		}));
	};

	const handleMouseUp = () => {
		setResizingColumn(null);
	};

	useEffect(() => {
		if (resizingColumn) {
			document.addEventListener('mousemove', handleMouseMove);
			document.addEventListener('mouseup', handleMouseUp);
			return () => {
				document.removeEventListener('mousemove', handleMouseMove);
				document.removeEventListener('mouseup', handleMouseUp);
			};
		}
		//eslint-disable-next-line
	}, [resizingColumn]);

	useEffect(() => {
		setBaseData(data);
		setFormatedData(data);
		setIsInitialized(false)
		// setIsSorting(false)
	}, [data]);

	useEffect(() => {
		return () => setIsInitialized(false)
	}, [])

	function getDataByPath(data: any, tableHeaders: any[], path: number[]): any {
		let current = Array.isArray(data) ? data : data?.[tableHeaders[0]?.rowKey];
		let obj = null;
		for (let i = 0; i < path.length; i++) {
			if (!current || !Array.isArray(current)) {
				return null;
			}
			obj = current[path[i]];
			if (i < path.length - 1) {
				const nextLevel = i + 1;
				const nextRowKey = tableHeaders[nextLevel]?.rowKey;
				current = obj?.[nextRowKey];
			}
		}
		return obj;
	}

	useEffect(() => {
		if (onSelectionChange) {
			const filteredPaths = selectedRowPaths.filter(path => {
				const level = path.length - 1;
				return tableHeaders[level]?.enableCheckbox;
			});
			const selectedData = filteredPaths
				.map(path => getDataByPath(formatedData, tableHeaders, path))
				.filter(Boolean);
			onSelectionChange(selectedData);
		}
		// eslint-disable-next-line
	}, [selectedRowPaths, formatedData]);

	// Check if we should show totals
	const showTotals = formatedData?.[totalKey] &&
		!(formatedData?.length === 0 || formatedData[tableHeaders[0]?.rowKey]?.length === 0);

	return (
		<TableContainer sx={{
			width: '100%',
			maxHeight: '600px',
			overflowX: 'auto',
			backgroundColor: 'white',
			display: 'block',
			position: 'relative'
		}}
			className="dynamic-reports"
		>
			<Table
				aria-label="collapsible table"
				sx={{
					borderCollapse: "collapse",
					width: "max-content",
					tableLayout: 'fixed',
					marginBottom: showTotals ? '0px' : '10px'
				}}
			>
				<TableHead sx={{ position: 'sticky', top: 0, zIndex: 10, backgroundColor: 'white' }}>
					<TableRow sx={{ zIndex: 11, backgroundColor: 'white' }}>
						{enableCheckbox && (
							<TableCell sx={{
								width: `${getCheckboxColumnWidth()}px`,
								minWidth: `${getCheckboxColumnWidth()}px`,
								maxWidth: `${getCheckboxColumnWidth()}px`,
								height: headerHeight,
								padding: '8px',
								position: 'sticky',
								left: 0,
								backgroundColor: 'white',
								zIndex: 9999
							}}>
								<Checkbox
									checked={allSelected}
									indeterminate={!allSelected && someSelected}
									onChange={e => handleHeaderCheckboxChange(e.target.checked)}
								/>
							</TableCell>
						)}
						{headers.map((obj, index) => {
							const enableSorting = obj?.enableSorting ?? true;
							const width = columnWidths[t(obj.label)] || 100;

							return (
								<TableCell
									key={index}
									className="dynamic-report-header"
									sx={{
										width: `${width}px`,
										minWidth: `${width}px`,
										maxWidth: `${width}px`,
										height: headerHeight,
										position: 'relative',
										padding: '8px',
										overflow: 'hidden'
									}}
								>
									<Box
										ref={(el) => headerRefs.current[obj.name] = el}
										sx={{
											display: 'flex',
											justifyContent: 'space-between',
											height: headerHeight,
											alignItems: 'center',
											width: 'calc(100% - 8px)',
											overflow: 'hidden',
											paddingRight: '0px'
										}}
									>
										<Typography
											type="s3"
											weight="medium"
											color='theme.primary'
											sx={{
												overflow: 'hidden',
												textOverflow: 'ellipsis',
												whiteSpace: 'nowrap',
												flex: 1,
												minWidth: 0,
												lineHeight: 'unset'
											}}
										>
											{typeof obj?.label === 'string' ? t(obj?.label) : obj?.label}
										</Typography>
										{enableSorting && (
											<IconButton
												size="small"
												sx={{
													padding: '4px 0px'
												}}
												onClick={(/* e */) => {
													// e.preventDefault();
													setIsSorting(true)
													setFilter(filter == obj.name ? null : obj.name)
													handleSort && handleSort(obj.name, filter == obj.name ? 'asc' : 'desc');
												}}
											>
												{filter == obj.name ? <ArrowUp fontSize='small' /> : <ArrowDown fontSize='small' />}
											</IconButton>
										)}
									</Box>
									{/* Resize Handle */}
									<Box
										className="dynamic-report-resize-handle"
										onMouseDown={(e) => handleMouseDown(e, t(obj.label))}
										sx={{
											position: 'absolute',
											right: 0,
											top: 0,
											bottom: 0,
											width: '16px',
											cursor: 'col-resize',
											backgroundColor: 'transparent',
											'&:hover': {
												backgroundColor: 'transparent',
												'&::before, &::after': {
													backgroundColor: 'transparent'
												}
											},
											'&::before': {
												content: '""',
												position: 'absolute',
												left: '2px',
												top: 0,
												bottom: 0,
												width: '2px',
												backgroundColor: 'transparent',
												transition: 'background-color 0.2s ease'
											},
											'&::after': {
												content: '""',
												position: 'absolute',
												right: '2px',
												top: 0,
												bottom: 0,
												width: '2px',
												backgroundColor: 'transparent',
												transition: 'background-color 0.2s ease'
											},
											zIndex: 2,
											userSelect: 'none'
										}}
									/>
								</TableCell>
							);
						})}
					</TableRow>
					{enableSearch && (
						<TableRow>
							{enableCheckbox && (
								<TableCell sx={{
									width: `${getCheckboxColumnWidth()}px`,
									minWidth: `${getCheckboxColumnWidth()}px`,
									maxWidth: `${getCheckboxColumnWidth()}px`,
									height: headerHeight,
									padding: '0px',
									position: 'sticky',
									left: 0,
									backgroundColor: 'white',
									zIndex: 9999
								}} />
							)}
							{headers.map((obj, index) => {
								const width = columnWidths[t(obj.label)] || 50;
								return (
									<TableCell
										key={obj?.label}
										className="dynamic-report-search"
										sx={{
											width: `${width}px`,
											minWidth: `${width}px`,
											maxWidth: `${width}px`,
											padding: '8px'
										}}
									>
										<SearchBar sx={{
											padding: '4px !important',
										}}
											InputProps={{
												sx: {
													marginLeft: '0.5rem !important',
												}
											}}
											handleSearch={(e) => handleSearch(e, obj.name, headers[index])} />
									</TableCell>
								);
							})}
						</TableRow>
					)}

					{/* Move afterHeader inside TableHead */}
					{afterHeader && (
						<TableRow>
							<TableCell
								colSpan={headers.length + (enableCheckbox ? 1 : 0)}
								sx={{
									padding: 0,
									borderBottom: 'none',
									backgroundColor: 'white',
								}}
							>
								{React.createElement(afterHeader, {
									columnWidths,
								})}
							</TableCell>
						</TableRow>
					)}
				</TableHead>



				{formatedData && (
					<TableBody>
						{Array.isArray(formatedData) ? formatedData?.map((row, index, arr) => (
							<DynamicRow
								key={row.id || index}
								level={0}
								rowPath={[index]}
								rowData={row}
								tableHeaders={tableHeaders}
								totalHeaders={totalHeaders}
								headers={headers}
								collapsAll={collapsLevel >= index + 1}
								collapsLevel={collapsLevel}
								isIncrese={!!isIncrese}
								data={formatedData}
								selectedRowPaths={selectedRowPaths}
								onCheckboxChange={handleCheckboxChange}
								dataSlice={arr}
								dataIndex={index}
								enableCheckBox={enableCheckbox}
								rowHeight={rowHeight}
								columnWidths={columnWidths}
							/>
						)) :
							tableHeaders[0]?.rowKey && (
								<>
									{formatedData[tableHeaders[0]?.rowKey]?.map((row: any, index: number, arr: any[]) => (
										<DynamicRow
											key={row.id || index}
											level={0}
											rowPath={[index]}
											rowData={row}
											tableHeaders={tableHeaders}
											totalHeaders={totalHeaders}
											headers={headers}
											collapsAll={collapsLevel >= index + 1}
											collapsLevel={collapsLevel}
											isIncrese={!!isIncrese}
											data={formatedData}
											selectedRowPaths={selectedRowPaths}
											onCheckboxChange={handleCheckboxChange}
											dataSlice={arr}
											dataIndex={index}
											enableCheckBox={enableCheckbox}
											rowHeight={rowHeight}
											columnWidths={columnWidths}
										/>
									))}
								</>
							)
						}
					</TableBody>
				)}
			</Table>

			{/* Fixed Totals Row */}
			{showTotals && (
				<Box
					sx={{
						// position: 'sticky',
						bottom: '-2px',
						// left: 0,
						width: 'max-content',
						backgroundColor: totalHeaders?.[0]?.bgColor || '#FAFAFA',
						borderTop: '1px solid #e0e0e0',
						// zIndex: 9
					}}
				>
					<Table
						sx={{
							width: "max-content",
							tableLayout: 'fixed',
							marginBottom: 0
						}}
					>
						<TableBody>
							<TableRow>
								{enableCheckbox && (
									<TableCell sx={{
										width: `${getCheckboxColumnWidth()}px`,
										minWidth: `${getCheckboxColumnWidth()}px`,
										maxWidth: `${getCheckboxColumnWidth()}px`,
										padding: '8px',
										position: 'sticky',
										left: 0,
										backgroundColor: totalHeaders?.[0]?.bgColor || '#FAFAFA',
										zIndex: 10,
										borderBottom: 'none'
									}} />
								)}
								{totalHeaders?.map((header, index) => {
									if (header.joinWithLeft) return null;
									const cellsToMerge = totalHeaders.slice(index).findIndex((h: any) => !h.joinWithLeft && h !== header) == -1 ? totalHeaders.slice(index)?.length : totalHeaders.slice(index).findIndex((h: any) => !h.joinWithLeft && h !== header);
									const colSpan = header.mergeCells ? cellsToMerge || 1 : 1;

									// Calculate merged width
									let totalWidth = 0;
									for (let i = index; i < index + colSpan && i < headers.length; i++) {
										totalWidth += columnWidths[t(headers[i].label)] || 100;
									}

									return (
										<TableCell
											key={index}
											className="dynamic-report-row"
											colSpan={colSpan}
											sx={{
												paddingLeft: '8px',
												width: `${totalWidth}px`,
												minWidth: `${totalWidth}px`,
												maxWidth: `${totalWidth}px`,
												borderBottom: 'none'
											}}
										>
											<Box sx={{
												display: 'flex',
												alignItems: 'center',
												justifyContent: `${header?.position ? header?.position : 'start'}`,
												width: '100%'
											}}>
												{header?.render ? header.render(formatedData?.[totalKey][header.name], formatedData) : (
													<Typography type="s4" weight="bold" color='theme.neutral.1000'>
														{getNestedValue(formatedData?.[totalKey], header)}
													</Typography>
												)}
											</Box>
										</TableCell>
									);
								})}
							</TableRow>
						</TableBody>
					</Table>
				</Box>
			)}

			{(data?.length === 0 || data?.[tableHeaders[0]?.rowKey]?.length === 0) && renderEmptyRowsFallback(t('common.noData'))}
		</TableContainer>
	);
}

export default ReportTable